// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { Holding } from '@daml.js/splice-api-token-holding-v1-1.0.0/lib/Splice/Api/Token/HoldingV1';
import { BridgeUserAgreement } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Agreement/User/module';
import { WithdrawAttestation } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Attestation/Withdraw/module';
import { useLedgerApiClient } from '../../../../contexts/LedgerApiContext';
import { ContractId} from '@daml/types';
import getContextContracts from '../../../queries/bridge/useContextContracts';
import { useParties } from '../../../../contexts/PartiesContext';
import { useUtilityRegistryApiClient } from '../../../../contexts/UtilityApiContext';

const useBurnAndAttest: () => UseMutationResult<
  [ContractId<WithdrawAttestation>, string] | undefined,
  string,
  {
    bridgeUserAgreementCid: ContractId<BridgeUserAgreement>;
    amount: string;
    destinationDomain: string;
    destinationRecipient: string;
    holdingCids: ContractId<Holding>[];
    totalHoldingAmount: string;
    reference: string;
  }
> = () => {
  const ledgerApi = useLedgerApiClient();
  const utilityRegistryApi = useUtilityRegistryApiClient();
  const requestId: string = uuidv4();
  const { party, crossChainRepresentative } = useParties();

  return useMutation({
    mutationKey: ['burnAndAttest', ledgerApi],
    mutationFn: async ({
      bridgeUserAgreementCid,
      amount,
      destinationDomain,
      destinationRecipient,
      holdingCids,
      totalHoldingAmount,
      reference,
    }) => {
      const output = {
        owner: party,
        amount: String(parseFloat(totalHoldingAmount) - parseFloat(amount)),
      };
      const inputHoldingCids = holdingCids.map(cid => cid as string);

      const contextContracts = await getContextContracts(
        utilityRegistryApi,
        crossChainRepresentative,
        inputHoldingCids,
        [output]
      );

      return ledgerApi.burnAndAttest(
        bridgeUserAgreementCid,
        amount,
        destinationDomain,
        destinationRecipient,
        holdingCids,
        reference,
        contextContracts.factoryCid,
        contextContracts.contextContractIds,
        requestId,
        contextContracts.requiredDisclosedContracts,
      );
    },
  });
};

export default useBurnAndAttest;
